import type { Route, RoutePoint, RouteStorageProvider, StorageResult } from '../../types/storage.js';

export class LocalStorageProvider implements RouteStorageProvider {
  private storageKey = 'cycling-buddy-routes';

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private getStoredRoutes(): Route[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to parse stored routes:', error);
      return [];
    }
  }

  private setStoredRoutes(routes: Route[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(routes));
    } catch (error) {
      console.error('Failed to store routes:', error);
    }
  }

  async saveRoute(route: Omit<Route, 'id' | 'created'>): Promise<StorageResult<Route>> {
    try {
      const routes = this.getStoredRoutes();
      const now = new Date();
      const newRoute: Route = {
        ...route,
        id: this.generateId(),
        created: now,
        updated: now
      };
      
      routes.push(newRoute);
      this.setStoredRoutes(routes);

      return {
        success: true,
        data: newRoute
      };
    } catch (error) {
      console.error('Failed to save route to localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async getRoute(id: string, _includeFullData: boolean = false): Promise<StorageResult<Route>> {
    try {
      const routes = this.getStoredRoutes();
      const route = routes.find(r => r.id === id && !r.deleted);

      if (!route) {
        return {
          success: false,
          error: 'Route not found'
        };
      }

      return {
        success: true,
        data: route
      };
    } catch (error) {
      console.error('Failed to get route from localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async getRoutes(userId: string): Promise<StorageResult<Route[]>> {
    try {
      const routes = this.getStoredRoutes();
      const userRoutes = routes.filter(r => r.userId === userId && !r.deleted);

      return {
        success: true,
        data: userRoutes
      };
    } catch (error) {
      console.error('Failed to get routes from localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async deleteRoute(id: string): Promise<StorageResult<boolean>> {
    try {
      const routes = this.getStoredRoutes();
      const routeIndex = routes.findIndex(r => r.id === id);

      if (routeIndex === -1) {
        return {
          success: false,
          error: 'Route not found'
        };
      }

      // Soft delete
      routes[routeIndex].deleted = true;
      this.setStoredRoutes(routes);

      return {
        success: true,
        data: true
      };
    } catch (error) {
      console.error('Failed to delete route from localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async updateRoute(id: string, routeUpdate: Partial<Route>): Promise<StorageResult<Route>> {
    try {
      const routes = this.getStoredRoutes();
      const routeIndex = routes.findIndex(r => r.id === id);

      if (routeIndex === -1) {
        return {
          success: false,
          error: 'Route not found'
        };
      }

      const updatedRoute = { 
        ...routes[routeIndex], 
        ...routeUpdate, 
        updated: new Date() // Always update the timestamp when route is modified
      };
      routes[routeIndex] = updatedRoute;
      this.setStoredRoutes(routes);

      return {
        success: true,
        data: updatedRoute
      };
    } catch (error) {
      console.error('Failed to update route in localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async updateRoutePoint(routeId: string, pointId: string, pointData: Partial<RoutePoint>): Promise<StorageResult<RoutePoint>> {
    try {
      const routes = this.getStoredRoutes();
      const routeIndex = routes.findIndex(r => r.id === routeId);

      if (routeIndex === -1) {
        return {
          success: false,
          error: 'Route not found'
        };
      }

      const route = routes[routeIndex];
      const pointIndex = route.points.findIndex(p => p.id === pointId);

      if (pointIndex === -1) {
        return {
          success: false,
          error: 'Route point not found'
        };
      }

      // Update the route point with new data
      const updatedPoint = { 
        ...route.points[pointIndex], 
        ...pointData 
      };
      
      route.points[pointIndex] = updatedPoint;
      route.updated = new Date(); // Update route timestamp
      
      this.setStoredRoutes(routes);

      return {
        success: true,
        data: updatedPoint
      };
    } catch (error) {
      console.error('Failed to update route point in localStorage:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Full route data methods (stubs for localStorage - not implemented)
  async saveFullRouteData(_routeId: string, _fullData: any): Promise<StorageResult<any>> {
    console.warn('Full route data storage not implemented for localStorage provider');
    return {
      success: false,
      error: 'Full route data storage not supported in localStorage'
    };
  }

  async getFullRouteData(_routeId: string): Promise<StorageResult<any>> {
    console.warn('Full route data loading not implemented for localStorage provider');
    return {
      success: true,
      data: null // No full route data available
    };
  }

  async updateFullRouteData(_routeId: string, _fullData: any): Promise<StorageResult<any>> {
    console.warn('Full route data update not implemented for localStorage provider');
    return {
      success: false,
      error: 'Full route data updates not supported in localStorage'
    };
  }
}
